'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppIcon from '@/components/ui/AppIcon';

interface QuoteForm {
  serviceType: string;
  propertySize: string;
  frequency: string;
  extras: string[];
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

interface SiteVisitForm {
  name: string;
  email: string;
  phone: string;
  address: string;
  preferredDate: string;
  preferredTime: string;
  notes: string;
}

const SERVICE_TYPES = [
  { key: 'standard', label: 'Standard Clean', description: 'Regular maintenance cleaning', basePrice: 120 },
  { key: 'deep', label: 'Deep Clean', description: 'Thorough top-to-bottom cleaning', basePrice: 220 },
  { key: 'moveinout', label: 'Move In/Out', description: 'Complete move-in or move-out clean', basePrice: 280 },
  { key: 'office', label: 'Office Cleaning', description: 'Commercial office space cleaning', basePrice: 180 },
];

const PROPERTY_SIZES = [
  { key: 'studio', label: 'Studio', multiplier: 0.8, category: 'residential' },
  { key: '1bed', label: '1 Bedroom', multiplier: 1.0, category: 'residential' },
  { key: '2bed', label: '2 Bedrooms', multiplier: 1.3, category: 'residential' },
  { key: '3bed', label: '3 Bedrooms', multiplier: 1.6, category: 'residential' },
  { key: '4bed', label: '4 Bedrooms', multiplier: 1.9, category: 'residential' },
  { key: '5plus', label: '5+ Bedrooms', multiplier: 2.3, category: 'residential' },
  { key: 'office-small', label: 'Small Office', description: 'Up to 1,000 sq ft', multiplier: 1.485, category: 'office' },
  { key: 'office-medium', label: 'Medium Office', description: '1,000–2,500 sq ft', multiplier: 2.16, category: 'office' },
  { key: 'office-large', label: 'Large Office', description: '2,500–5,000 sq ft', multiplier: 2.97, category: 'office' },
  { key: 'office-enterprise', label: 'Enterprise Office', description: '5,000+ sq ft', multiplier: 4.05, category: 'office' },
];

const FREQUENCIES = [
  { key: 'onetime', label: 'One-Time', discount: 0 },
  { key: 'weekly', label: 'Weekly', discount: 0.2 },
  { key: 'biweekly', label: 'Bi-Weekly', discount: 0.15 },
  { key: 'monthly', label: 'Monthly', discount: 0.1 },
];

const EXTRAS = [
  { key: 'inside_fridge', label: 'Inside Fridge', price: 35 },
  { key: 'inside_oven', label: 'Inside Oven', price: 40 },
  { key: 'inside_cabinets', label: 'Inside Cabinets', price: 50 },
  { key: 'laundry', label: 'Laundry (wash & fold)', price: 45 },
  { key: 'windows', label: 'Interior Windows', price: 60 },
  { key: 'garage', label: 'Garage Sweep', price: 55 },
];

const TIME_SLOTS = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
];

function calculatePrice(form: QuoteForm): number {
  const service = SERVICE_TYPES.find(s => s.key === form.serviceType);
  const size = PROPERTY_SIZES.find(s => s.key === form.propertySize);
  const freq = FREQUENCIES.find(f => f.key === form.frequency);

  if (!service || !size || !freq) return 0;

  let price = service.basePrice * size.multiplier;
  price = price * (1 - freq.discount);

  form.extras.forEach(extraKey => {
    const extra = EXTRAS.find(e => e.key === extraKey);
    if (extra) price += extra.price;
  });

  return Math.round(price);
}

export default function InstantQuotePage() {
  const router = useRouter();
  const [form, setForm] = useState<QuoteForm>({
    serviceType: 'standard',
    propertySize: '2bed',
    frequency: 'biweekly',
    extras: [],
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Site visit state
  const [showSiteVisit, setShowSiteVisit] = useState(false);
  const [siteVisitForm, setSiteVisitForm] = useState<SiteVisitForm>({
    name: '',
    email: '',
    phone: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    notes: '',
  });
  const [siteVisitLoading, setSiteVisitLoading] = useState(false);
  const [siteVisitSubmitted, setSiteVisitSubmitted] = useState(false);
  const [siteVisitError, setSiteVisitError] = useState<string | null>(null);

  const estimatedPrice = calculatePrice(form);
  const selectedService = SERVICE_TYPES.find(s => s.key === form.serviceType);
  const selectedFreq = FREQUENCIES.find(f => f.key === form.frequency);

  const toggleExtra = useCallback((key: string) => {
    setForm(prev => ({
      ...prev,
      extras: prev.extras.includes(key)
        ? prev.extras.filter(e => e !== key)
        : [...prev.extras, key],
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('Please fill in your name and email.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('quote_requests').insert({
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service_type: form.serviceType,
        property_size: form.propertySize,
        frequency: form.frequency,
        extras: form.extras,
        estimated_price: estimatedPrice,
        notes: form.notes || null,
        status: 'new',
      });
      if (dbError) {
        setError('Something went wrong. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSiteVisitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteVisitForm.name || !siteVisitForm.email || !siteVisitForm.address || !siteVisitForm.preferredDate || !siteVisitForm.preferredTime) {
      setSiteVisitError('Please fill in all required fields.');
      return;
    }
    setSiteVisitLoading(true);
    setSiteVisitError(null);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('site_visit_requests').insert({
        name: siteVisitForm.name,
        email: siteVisitForm.email,
        phone: siteVisitForm.phone || null,
        address: siteVisitForm.address,
        preferred_date: siteVisitForm.preferredDate,
        preferred_time: siteVisitForm.preferredTime,
        notes: siteVisitForm.notes || null,
        status: 'pending',
      });
      if (dbError) {
        setSiteVisitError('Something went wrong. Please try again.');
        return;
      }
      setSiteVisitSubmitted(true);
    } catch {
      setSiteVisitError('Something went wrong. Please try again.');
    } finally {
      setSiteVisitLoading(false);
    }
  };

  const handleBookNow = () => {
    const params = new URLSearchParams({
      service: form.serviceType,
      propertySize: form.propertySize,
      frequency: form.frequency,
      ...(form.address ? { address: form.address } : {}),
    });
    router.push(`/homepage#booking?${params.toString()}`);
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFBFC' }}>
        <div className="max-w-md w-full mx-4 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-10 border border-gray-100">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8F5E9' }}>
              <AppIcon name="CheckCircleIcon" size={40} variant="solid" className="text-green-700" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Quote Submitted!</h2>
            <p className="text-gray-500 mb-4">Thanks, <strong>{form.name}</strong>! Your estimated quote is:</p>
            <div className="text-5xl font-bold mb-2" style={{ color: '#2E7D32' }}>${estimatedPrice}</div>
            <p className="text-sm text-gray-400 mb-8">Our team will contact you at <strong>{form.email}</strong> shortly to confirm your booking.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleBookNow}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all"
                style={{ backgroundColor: '#2E7D32' }}
              >
                <AppIcon name="CalendarIcon" size={18} variant="outline" />
                Book Now with This Quote
              </button>
              <Link href="/homepage" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFBFC' }}>
      {/* Header */}
      <div className="py-10 px-4" style={{ backgroundColor: '#2E7D32' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-4">
            <AppIcon name="CalculatorIcon" size={16} variant="outline" className="text-green-100" />
            <span className="text-green-100 text-sm font-medium">Instant Price Calculator</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Get Your Instant Quote</h1>
          <p className="text-green-100 text-base max-w-xl mx-auto">Customize your cleaning service and see your price update in real time. No hidden fees.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Column */}
          <div className="lg:col-span-2">
            {/* Step Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setStep(1)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  step === 1 ? 'text-white shadow' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                }`}
                style={step === 1 ? { backgroundColor: '#2E7D32' } : {}}
              >
                1. Service Details
              </button>
              <button
                onClick={() => setStep(2)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  step === 2 ? 'text-white shadow' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                }`}
                style={step === 2 ? { backgroundColor: '#2E7D32' } : {}}
              >
                2. Your Info
              </button>
            </div>

            {step === 1 && (
              <div className="space-y-6">
                {/* Service Type */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AppIcon name="SparklesIcon" size={18} variant="solid" className="text-green-700" />
                    Service Type
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SERVICE_TYPES.map(service => (
                      <button
                        key={service.key}
                        onClick={() => setForm(prev => ({ ...prev, serviceType: service.key }))}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          form.serviceType === service.key
                            ? 'border-green-700 bg-green-50' :'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="font-semibold text-gray-800 text-sm">{service.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{service.description}</div>
                        <div className="text-sm font-bold mt-2" style={{ color: '#2E7D32' }}>from ${service.basePrice}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Property Size */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AppIcon name="HomeIcon" size={18} variant="solid" className="text-green-700" />
                    Property Size
                  </h3>
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Residential</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {PROPERTY_SIZES.filter(s => s.category === 'residential').map(size => (
                        <button
                          key={size.key}
                          onClick={() => setForm(prev => ({ ...prev, propertySize: size.key }))}
                          className={`py-3 px-2 rounded-lg border-2 text-center text-sm font-medium transition-all ${
                            form.propertySize === size.key
                              ? 'border-green-700 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                          }`}
                        >
                          {size.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Office</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PROPERTY_SIZES.filter(s => s.category === 'office').map(size => (
                        <button
                          key={size.key}
                          onClick={() => setForm(prev => ({ ...prev, propertySize: size.key }))}
                          className={`py-3 px-3 rounded-lg border-2 text-center text-sm font-medium transition-all ${
                            form.propertySize === size.key
                              ? 'border-green-700 bg-green-50 text-green-800' : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                          }`}
                        >
                          <div>{size.label}</div>
                          {size.description && <div className="text-xs font-normal mt-0.5 opacity-75">{size.description}</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Frequency */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AppIcon name="CalendarDaysIcon" size={18} variant="solid" className="text-green-700" />
                    Cleaning Frequency
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {FREQUENCIES.map(freq => (
                      <button
                        key={freq.key}
                        onClick={() => setForm(prev => ({ ...prev, frequency: freq.key }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          form.frequency === freq.key
                            ? 'border-green-700 bg-green-50' :'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="font-semibold text-sm text-gray-800">{freq.label}</div>
                        {freq.discount > 0 && (
                          <div className="text-xs font-medium mt-1" style={{ color: '#2E7D32' }}>
                            Save {Math.round(freq.discount * 100)}%
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Extras */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AppIcon name="PlusCircleIcon" size={18} variant="solid" className="text-green-700" />
                    Add-On Services
                    <span className="text-xs text-gray-400 font-normal">(optional)</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {EXTRAS.map(extra => (
                      <button
                        key={extra.key}
                        onClick={() => toggleExtra(extra.key)}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                          form.extras.includes(extra.key)
                            ? 'border-green-700 bg-green-50' :'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            form.extras.includes(extra.key) ? 'border-green-700 bg-green-700' : 'border-gray-300'
                          }`}>
                            {form.extras.includes(extra.key) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{extra.label}</span>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: '#2E7D32' }}>+${extra.price}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="w-full py-4 rounded-xl font-semibold text-white text-base transition-all shadow-md hover:shadow-lg active:scale-95"
                  style={{ backgroundColor: '#2E7D32' }}
                >
                  Continue to Your Info →
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AppIcon name="UserIcon" size={18} variant="solid" className="text-green-700" />
                    Your Contact Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Jane Smith"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="jane@example.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(612) 555-0100"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Address <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        value={form.address}
                        onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Main St, Minneapolis, MN 55401"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                      <textarea
                        value={form.notes}
                        onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any special requests or details about your home..."
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm resize-none"
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AppIcon name="ExclamationCircleIcon" size={18} variant="solid" />
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 rounded-xl font-semibold text-gray-600 bg-white border-2 border-gray-200 hover:border-gray-300 transition-all"
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-2 flex-grow py-4 rounded-xl font-semibold text-white text-base transition-all shadow-md hover:shadow-lg active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#2E7D32' }}
                  >
                    {loading ? 'Submitting...' : `Get My Quote — $${estimatedPrice}`}
                  </button>
                </div>
              </form>
            )}

            {/* Site Visit Section */}
            <div className="mt-8">
              <div className="bg-white rounded-xl border-2 border-dashed border-green-200 shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8F5E9' }}>
                      <AppIcon name="BuildingOfficeIcon" size={24} variant="solid" className="text-green-700" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg">Need a Custom Estimate?</h3>
                      <p className="text-gray-500 text-sm mt-1">For large offices, unique spaces, or complex jobs — schedule a free on-site visit and get a precise quote tailored to your needs.</p>
                      {!showSiteVisit && !siteVisitSubmitted && (
                        <button
                          onClick={() => setShowSiteVisit(true)}
                          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-all shadow hover:shadow-md active:scale-95"
                          style={{ backgroundColor: '#2E7D32' }}
                        >
                          <AppIcon name="CalendarDaysIcon" size={16} variant="outline" />
                          Schedule a Site Visit for Estimate
                        </button>
                      )}
                    </div>
                  </div>

                  {siteVisitSubmitted && (
                    <div className="mt-5 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <AppIcon name="CheckCircleIcon" size={22} variant="solid" className="text-green-700 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800 text-sm">Site Visit Requested!</p>
                        <p className="text-green-700 text-xs mt-0.5">We&apos;ll confirm your appointment at <strong>{siteVisitForm.email}</strong> within 24 hours.</p>
                      </div>
                    </div>
                  )}

                  {showSiteVisit && !siteVisitSubmitted && (
                    <form onSubmit={handleSiteVisitSubmit} className="mt-6 space-y-4">
                      <div className="border-t border-gray-100 pt-5">
                        <h4 className="font-semibold text-gray-700 text-sm mb-4 flex items-center gap-2">
                          <AppIcon name="MapPinIcon" size={16} variant="solid" className="text-green-700" />
                          Schedule Your Free Site Visit
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={siteVisitForm.name}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Jane Smith"
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              value={siteVisitForm.email}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="jane@example.com"
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-gray-400 font-normal">(optional)</span></label>
                            <input
                              type="tel"
                              value={siteVisitForm.phone}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="(612) 555-0100"
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Property Address <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={siteVisitForm.address}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, address: e.target.value }))}
                              placeholder="123 Main St, Minneapolis, MN"
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date <span className="text-red-500">*</span></label>
                            <input
                              type="date"
                              value={siteVisitForm.preferredDate}
                              min={today}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, preferredDate: e.target.value }))}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time <span className="text-red-500">*</span></label>
                            <select
                              value={siteVisitForm.preferredTime}
                              onChange={e => setSiteVisitForm(prev => ({ ...prev, preferredTime: e.target.value }))}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm bg-white"
                              required
                            >
                              <option value="">Select a time</option>
                              {TIME_SLOTS.map(slot => (
                                <option key={slot} value={slot}>{slot}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                          <textarea
                            value={siteVisitForm.notes}
                            onChange={e => setSiteVisitForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Describe your space, special requirements, or any questions..."
                            rows={3}
                            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-700/30 focus:border-green-700 text-sm resize-none"
                          />
                        </div>
                      </div>

                      {siteVisitError && (
                        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          <AppIcon name="ExclamationCircleIcon" size={18} variant="solid" />
                          {siteVisitError}
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowSiteVisit(false)}
                          className="px-5 py-3 rounded-lg font-semibold text-gray-600 bg-white border-2 border-gray-200 hover:border-gray-300 transition-all text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={siteVisitLoading}
                          className="flex-1 py-3 rounded-lg font-semibold text-white text-sm transition-all shadow hover:shadow-md active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#2E7D32' }}
                        >
                          {siteVisitLoading ? 'Submitting...' : 'Request Site Visit'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-5" style={{ backgroundColor: '#2E7D32' }}>
                  <div className="text-green-100 text-sm font-medium mb-1">Estimated Price</div>
                  <div className="text-4xl font-bold text-white">${estimatedPrice}</div>
                  {selectedFreq && selectedFreq.discount > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                      <AppIcon name="TagIcon" size={12} variant="solid" className="text-green-100" />
                      <span className="text-green-100 text-xs font-medium">{Math.round(selectedFreq.discount * 100)}% recurring discount applied</span>
                    </div>
                  )}
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Service</span>
                    <span className="font-medium text-gray-800">{selectedService?.label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Size</span>
                    <span className="font-medium text-gray-800">{PROPERTY_SIZES.find(s => s.key === form.propertySize)?.label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Frequency</span>
                    <span className="font-medium text-gray-800">{FREQUENCIES.find(f => f.key === form.frequency)?.label}</span>
                  </div>
                  {form.extras.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">Add-ons</div>
                      {form.extras.map(key => {
                        const extra = EXTRAS.find(e => e.key === key);
                        return extra ? (
                          <div key={key} className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{extra.label}</span>
                            <span className="text-gray-700">+${extra.price}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-800">Total Estimate</span>
                      <span className="font-bold text-lg" style={{ color: '#2E7D32' }}>${estimatedPrice}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Final price confirmed after booking</p>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                {[
                  { icon: 'ShieldCheckIcon', text: 'Insured & Bonded' },
                  { icon: 'CheckBadgeIcon', text: 'Background Checked Staff' },
                  { icon: 'SparklesIcon', text: 'Eco-Friendly Products' },
                  { icon: 'StarIcon', text: '4.9★ Average Rating' },
                ].map(badge => (
                  <div key={badge.text} className="flex items-center gap-2">
                    <AppIcon name={badge.icon as any} size={16} variant="solid" className="text-green-700 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
